'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { projectPhoto } from '@/lib/projectPhoto';
import SitePhoto from '@/components/ui/SitePhoto';
import { SITE_PHOTOS } from '@/data/sitePhotos';
import CTAPrimary from '@/components/ui/CTAPrimary';
import CTASecondary from '@/components/ui/CTASecondary';
import ModularityDiagram from '@/components/ui/ModularityDiagram';
import ScrollReveal from '@/app/components/ScrollReveal';
import Breadcrumbs from '@/components/ui/Breadcrumbs';

// ─── DATA ─────────────────────────────────────────────────────────────────────

const SYSTEM_STAGES = [
  {
    id: 'design',
    number: '01',
    label: 'Design',
    description:
      'Modularity begins at the design stage. Components must be conceived as individual, serviceable units — not as a single monolithic form.',
  },
  {
    id: 'manufacturing',
    number: '02',
    label: 'Manufacturing',
    description:
      'Each component is manufactured to a documented standard. Controlled processes allow components to be reproduced consistently when replacement is needed.',
  },
  {
    id: 'assembly',
    number: '03',
    label: 'Assembly',
    description:
      'Assembly methods determine whether a component can be removed cleanly. How parts connect matters as much as what they are made of.',
  },
  {
    id: 'installation',
    number: '04',
    label: 'Installation',
    description:
      'Installation must account for future access. A product installed without thought for serviceability is not truly modular — regardless of how it was manufactured.',
  },
  {
    id: 'serviceability',
    number: '05',
    label: 'Serviceability',
    description:
      'The system is only modular if a component can be removed and replaced in the real world — without unnecessarily damaging the surrounding structure.',
  },
];

const WHY_MATTERS = [
  { label: 'Serviceability', note: 'A component can be repaired or replaced without dismantling the whole.' },
  { label: 'Replaceability', note: 'Individual parts can be sourced and substituted when needed.' },
  { label: 'Repeatability', note: 'Standardised manufacturing means components can be reproduced consistently.' },
  { label: 'Controlled Manufacturing', note: 'Documented processes reduce variation and support quality.' },
  { label: 'Installation Integrity', note: 'Thoughtful installation preserves the ability to service later.' },
  { label: 'Long-term Maintainability', note: 'The product remains manageable over its useful life.' },
];

// ─── PAGE ─────────────────────────────────────────────────────────────────────

// `journey`: the next-visual cards for this page, built on the server (see src/data/journeyItems.ts)
export default function ModularInteriorsContent({ journey }: { journey?: React.ReactNode } = {}) {
  return (
    <div className="bg-warm-white min-h-screen">

      {/* ── BREADCRUMBS ─────────────────────────────────────────── */}
      <div className="container-willow pt-8 pb-0">
        <Breadcrumbs
          items={[
            { label: 'Home', href: '/' },
            { label: 'Modular Interiors' },
          ]}
        />
      </div>

      {/* ── HERO ────────────────────────────────────────────────── */}
      <HeroSection />

      {/* ── DEFINITION ──────────────────────────────────────────── */}
      <DefinitionSection />

      {/* ── THE SYSTEM ──────────────────────────────────────────── */}
      <SystemSection />

      {/* ── COMPONENT INTERACTION ───────────────────────────────── */}
      <ComponentInteractionSection />

      {/* ── CUSTOM DIMENSIONS ───────────────────────────────────── */}
      <CustomDimensionsSection />

      {/* ── WHY THIS MATTERS ────────────────────────────────────── */}
      <WhyMattersSection />

      {/* ── CONNECTION TO MANUFACTURING ─────────────────────────── */}
      <ManufacturingConnectionSection />

      {/* ── CONNECTION TO HOME INTERIORS ────────────────────────── */}
      <HomeInteriorsConnectionSection />

      {/* ── LEARNING APPROACH ───────────────────────────────────── */}
      <LearningApproachSection />

      {/* ── NEXT VISUALS — where to look next (visual journey) ── */}
      {journey}

      {/* ── FINAL CTA ───────────────────────────────────────────── */}
      <FinalCTASection />

    </div>
  );
}

// ─── HERO ─────────────────────────────────────────────────────────────────────

function HeroSection() {
  return (
    <section aria-labelledby="modular-h1" className="border-b border-border overflow-hidden">
      <div className="container-willow py-16 md:py-0">
        <div className="grid lg:grid-cols-[1fr_1fr] lg:min-h-[80vh] items-stretch">

          {/* Left: Text */}
          <div className="flex flex-col justify-center gap-8 py-16 lg:py-24 lg:pr-16">
            <div className="flex items-center gap-3">
              <span className="accent-line" aria-hidden="true" />
              <span className="text-section-label">A DIFFERENT DEFINITION</span>
            </div>

            <div>
              <h1
                id="modular-h1"
                style={{
                  fontFamily: 'inherit',
                  fontSize: 'clamp(2.5rem, 5vw, 4.5rem)',
                  fontWeight: 700,
                  lineHeight: 1.0,
                  letterSpacing: '-0.04em',
                  color: 'var(--foreground)',
                }}
              >
                {/* Topic line inside the H1 so the heading says what the page is about; slogan below */}
                <span className="block text-section-label mb-3 leading-normal">Modular Interiors in Bangalore</span>
                Modular Doesn&apos;t<br />
                Just Mean<br />
                <span style={{ color: 'var(--primary)' }}>Factory-Made.</span>
              </h1>
            </div>

            <p className="text-body-lg" style={{ maxWidth: '34rem' }}>
              For Willow, modularity is a system — not a manufacturing method. It involves how a product is designed, built, assembled, installed and serviced.
            </p>

            {/* Chain */}
            <div className="flex flex-wrap items-center gap-2">
              {['DESIGN', 'MANUFACTURE', 'ASSEMBLY', 'INSTALL', 'SERVICE']?.map((s, i, arr) => (
                <React.Fragment key={s}>
                  <span
                    className="text-section-label"
                    style={{
                      color: s === 'MANUFACTURE' ? 'var(--primary)' : 'var(--secondary)',
                      opacity: s === 'MANUFACTURE' ? 1 : 0.6,
                      fontSize: '0.6rem',
                    }}
                  >
                    {s}
                  </span>
                  {i < arr?.length - 1 && (
                    <span
                      aria-hidden="true"
                      style={{ color: 'var(--primary)', opacity: 0.3, fontSize: '0.6rem' }}
                    >
                      ↓
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <CTAPrimary label="Start Your Project" href="/contact" />
              <CTASecondary label="Explore Home Interiors" href="/home-interiors" />
            </div>
          </div>

          {/* Right: Hero image */}
          <div className="relative hidden lg:block border-l border-border">
            <SitePhoto photo={SITE_PHOTOS.studioCabinet} aspectRatio="3/4" className="w-full h-full absolute inset-0" priority />
            <div
              className="absolute bottom-8 left-8 px-4 py-2"
              style={{ backgroundColor: 'rgba(23,22,20,0.72)', backdropFilter: 'blur(4px)' }}
            >
              <span className="text-section-label" style={{ color: 'var(--primary)', fontSize: '0.55rem' }}>
                WILLOW MODULAR INTERIORS · BANGALORE
              </span>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

// ─── DEFINITION ───────────────────────────────────────────────────────────────

function DefinitionSection() {
  return (
    <section
      aria-labelledby="definition-heading"
      style={{ backgroundColor: '#171614' }}
      className="border-b border-border"
    >
      <div className="container-willow py-16 md:py-24">
        <ScrollReveal>
          <div className="grid md:grid-cols-[1fr_1.6fr] gap-12 md:gap-20 items-start">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="block h-px w-8 flex-shrink-0" style={{ backgroundColor: '#AA7D53' }} aria-hidden="true" />
                <span className="text-section-label" style={{ color: 'var(--primary)' }}>THE WILLOW DEFINITION</span>
              </div>
              <h2
                id="definition-heading"
                style={{
                  color: '#FAF8F5',
                  fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                  fontWeight: 700,
                  lineHeight: 1.1,
                  letterSpacing: '-0.03em',
                }}
              >
                What Modular<br />Means Here.
              </h2>
            </div>

            <div className="flex flex-col gap-8">
              <blockquote
                style={{
                  borderLeft: '2px solid #AA7D53',
                  paddingLeft: '1.5rem',
                }}
              >
                <p
                  style={{
                    color: '#FAF8F5',
                    fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)',
                    fontWeight: 500,
                    lineHeight: 1.4,
                    letterSpacing: '-0.02em',
                    fontStyle: 'italic',
                  }}
                >
                  &ldquo;A product is modular when any component can be removed and replaced without unnecessarily damaging the surrounding system.&rdquo;
                </p>
              </blockquote>

              <p style={{ color: 'rgba(250,248,245,0.6)', fontSize: '1rem', lineHeight: 1.7 }}>
                This is not simply a description of furniture manufactured in a factory. Achieving this requires thinking about more than manufacturing alone.
              </p>

              <p style={{ color: 'rgba(250,248,245,0.45)', fontSize: '0.9375rem', lineHeight: 1.7 }}>
                It involves how a product is designed, how it is manufactured, how it is assembled, how it is installed — and whether it can be serviced in the real world, years after the project is complete.
              </p>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

// ─── THE SYSTEM ───────────────────────────────────────────────────────────────

function SystemSection() {
  const [activeStage, setActiveStage] = useState(0);
  const active = SYSTEM_STAGES?.[activeStage];

  return (
    <section
      aria-labelledby="system-heading"
      style={{ backgroundColor: '#F0ECE6' }}
      className="border-b border-border"
    >
      <div className="container-willow py-16 md:py-24">
        <ScrollReveal>
          <div className="flex items-center gap-3 mb-4">
            <span className="accent-line" aria-hidden="true" />
            <span className="text-section-label">THE SYSTEM</span>
          </div>
          <h2
            id="system-heading"
            style={{
              fontSize: 'clamp(1.75rem, 3.5vw, 3rem)',
              fontWeight: 700,
              lineHeight: 1.05,
              letterSpacing: '-0.035em',
              marginBottom: '3rem',
            }}
          >
            Five Stages.<br />One System.
          </h2>
        </ScrollReveal>

        {/* Desktop: interactive selector */}
        <div className="hidden lg:grid lg:grid-cols-[1fr_1.4fr] gap-0 border border-border">
          <div className="border-r border-border">
            {SYSTEM_STAGES?.map((stage, i) => (
              <button
                key={stage?.id}
                onClick={() => setActiveStage(i)}
                className="w-full text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                style={{
                  padding: '1.5rem 1.75rem',
                  borderBottom: i < SYSTEM_STAGES?.length - 1 ? '1px solid var(--border)' : 'none',
                  backgroundColor: activeStage === i ? '#171614' : 'transparent',
                }}
                aria-pressed={activeStage === i}
              >
                <div className="flex items-center gap-4">
                  <span
                    style={{
                      fontSize: '0.55rem',
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      color: activeStage === i ? 'var(--primary)' : 'rgba(170,125,83,0.4)',
                      width: '1.5rem',
                      flexShrink: 0,
                    }}
                  >
                    {stage?.number}
                  </span>
                  <span
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: activeStage === i ? '#FAF8F5' : 'var(--foreground)',
                    }}
                  >
                    {stage?.label}
                  </span>
                  {i < SYSTEM_STAGES?.length - 1 && (
                    <span
                      aria-hidden="true"
                      style={{
                        marginLeft: 'auto',
                        color: activeStage === i ? 'var(--primary)' : 'var(--border)',
                        fontSize: '0.75rem',
                      }}
                    >
                      ↓
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>

          <div
            style={{
              padding: '2.5rem',
              backgroundColor: '#171614',
              minHeight: '280px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <p
              style={{
                fontSize: '0.55rem',
                fontWeight: 700,
                letterSpacing: '0.15em',
                color: 'var(--primary)',
                marginBottom: '1rem',
                textTransform: 'uppercase',
              }}
            >
              STAGE {active?.number} OF {SYSTEM_STAGES?.length}
            </p>
            <h3
              style={{
                fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                fontWeight: 700,
                lineHeight: 1.1,
                letterSpacing: '-0.03em',
                color: '#FAF8F5',
                marginBottom: '1.25rem',
              }}
            >
              {active?.label}
            </h3>
            <p style={{ color: 'rgba(250,248,245,0.6)', fontSize: '1rem', lineHeight: 1.7 }}>
              {active?.description}
            </p>
            <div className="flex gap-1 mt-8" aria-hidden="true">
              {SYSTEM_STAGES?.map((_, i) => (
                <div
                  key={i}
                  style={{
                    height: '2px',
                    flex: 1,
                    backgroundColor: i <= activeStage ? '#AA7D53' : 'rgba(221,215,207,0.15)',
                    transition: 'background-color 0.2s',
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Mobile: stacked list */}
        <div className="lg:hidden border border-border">
          {SYSTEM_STAGES?.map((stage, i) => (
            <div
              key={stage?.id}
              style={{
                padding: '1.25rem 1.5rem',
                borderBottom: i < SYSTEM_STAGES?.length - 1 ? '1px solid var(--border)' : 'none',
              }}
            >
              <div className="flex items-start gap-4">
                <span
                  style={{
                    fontSize: '0.55rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    color: 'rgba(170,125,83,0.5)',
                    width: '1.5rem',
                    flexShrink: 0,
                    marginTop: '0.15rem',
                  }}
                >
                  {stage?.number}
                </span>
                <div>
                  <p
                    style={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: 'var(--foreground)',
                      marginBottom: '0.5rem',
                    }}
                  >
                    {stage?.label}
                  </p>
                  <p style={{ fontSize: '0.875rem', color: 'var(--secondary)', lineHeight: 1.6 }}>
                    {stage?.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── COMPONENT INTERACTION ────────────────────────────────────────────────────

function ComponentInteractionSection() {
  return (
    <section
      aria-labelledby="component-heading"
      className="bg-warm-white border-b border-border"
    >
      <div className="container-willow py-16 md:py-24">
        <div className="grid lg:grid-cols-[1fr_1.3fr] gap-12 lg:gap-20 items-start">

          <ScrollReveal>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="accent-line" aria-hidden="true" />
                <span className="text-section-label">COMPONENT → REPLACE</span>
              </div>
              <h2
                id="component-heading"
                style={{
                  fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)',
                  fontWeight: 700,
                  lineHeight: 1.05,
                  letterSpacing: '-0.035em',
                  marginBottom: '1.5rem',
                }}
              >
                Understanding<br />the Product<br />as a System.
              </h2>
              <p className="text-body-lg mb-5">
                A modular system should allow individual components to be understood, accessed and serviced — not just assembled and forgotten.
              </p>
              <p className="text-body" style={{ color: 'var(--secondary)' }}>
                The sequence below shows how Willow thinks about a modular product: from the complete installed unit, through its individual components, to the ability to remove and replace a part without damaging the surrounding structure.
              </p>

              <div
                className="mt-8 p-5 border"
                style={{ borderColor: 'var(--border)', backgroundColor: 'var(--stone)' }}
              >
                <p
                  style={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    color: 'var(--secondary)',
                    marginBottom: '0.75rem',
                    textTransform: 'uppercase',
                  }}
                >
                  THE SEQUENCE
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  {['COMPLETE PRODUCT', 'COMPONENTS', 'ASSEMBLY', 'INSTALLED', 'REMOVE', 'REPLACE']?.map((s, i, arr) => (
                    <React.Fragment key={s}>
                      <span
                        style={{
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          letterSpacing: '0.1em',
                          color: s === 'REPLACE' ? 'var(--primary)' : 'var(--foreground)',
                        }}
                      >
                        {s}
                      </span>
                      {i < arr?.length - 1 && (
                        <span
                          aria-hidden="true"
                          style={{ color: 'var(--primary)', opacity: 0.35, fontSize: '0.65rem' }}
                        >
                          →
                        </span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal>
            <div>
              <ModularityDiagram />
            </div>
          </ScrollReveal>

        </div>
      </div>
    </section>
  );
}

// ─── CUSTOM DIMENSIONS ────────────────────────────────────────────────────────

function CustomDimensionsSection() {
  return (
    <section
      aria-labelledby="custom-dimensions-heading"
      style={{ backgroundColor: '#171614' }}
      className="border-b border-border"
    >
      <div className="container-willow py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          <ScrollReveal>
            <div className="relative overflow-hidden" style={{ aspectRatio: '4/3' }}>
              <SitePhoto photo={SITE_PHOTOS.edgeBander} aspectRatio="4/3" className="w-full h-full" />
              <div
                className="absolute bottom-0 left-0 right-0 px-6 py-4"
                style={{ background: 'linear-gradient(to top, rgba(23,22,20,0.7) 0%, transparent 100%)' }}
              >
                <p className="text-section-label" style={{ color: 'var(--primary)', fontSize: '0.55rem' }}>
                  WILLOW MANUFACTURING · CUSTOM DIMENSIONS
                </p>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="block h-px w-8 flex-shrink-0" style={{ backgroundColor: '#AA7D53' }} aria-hidden="true" />
                <span className="text-section-label" style={{ color: 'var(--primary)' }}>CUSTOM DIMENSIONS</span>
              </div>
              <h2
                id="custom-dimensions-heading"
                style={{
                  color: '#FAF8F5',
                  fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                  fontWeight: 700,
                  lineHeight: 1.1,
                  letterSpacing: '-0.03em',
                  marginBottom: '1.5rem',
                }}
              >
                Custom Doesn&apos;t<br />Always Mean<br />Completely Custom.
              </h2>
              <p style={{ color: 'rgba(250,248,245,0.65)', fontSize: '1rem', lineHeight: 1.7, marginBottom: '1.25rem' }}>
                Custom dimensions do not necessarily require a completely custom manufacturing method every time.
              </p>
              <p style={{ color: 'rgba(250,248,245,0.45)', fontSize: '0.9375rem', lineHeight: 1.7, marginBottom: '1.25rem' }}>
                Willow can work with custom dimensions while using standardised manufacturing methods where appropriate. This is part of how modularity and repeatability coexist — even in projects where every space is different.
              </p>
              <p style={{ color: 'rgba(250,248,245,0.35)', fontSize: '0.875rem', lineHeight: 1.7 }}>
                The goal is not to force every project into a fixed catalogue. It is to apply controlled, documented processes to whatever the project requires.
              </p>
            </div>
          </ScrollReveal>

        </div>
      </div>
    </section>
  );
}

// ─── WHY THIS MATTERS ─────────────────────────────────────────────────────────

function WhyMattersSection() {
  return (
    <section
      aria-labelledby="why-matters-heading"
      style={{ backgroundColor: '#F0ECE6' }}
      className="border-b border-border"
    >
      <div className="container-willow py-16 md:py-24">
        <ScrollReveal>
          <div className="grid md:grid-cols-[1fr_1fr] gap-12 md:gap-20 items-end mb-14">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="accent-line" aria-hidden="true" />
                <span className="text-section-label">WHY THIS MATTERS</span>
              </div>
              <h2
                id="why-matters-heading"
                style={{
                  fontSize: 'clamp(1.75rem, 3.5vw, 3rem)',
                  fontWeight: 700,
                  lineHeight: 1.05,
                  letterSpacing: '-0.035em',
                }}
              >
                Built to Last<br />Beyond Installation.
              </h2>
            </div>
            <div>
              <p className="text-body-lg">
                Modularity is not a feature. It is a set of decisions made at every stage of the process — decisions that determine whether a product remains manageable over its useful life.
              </p>
            </div>
          </div>
        </ScrollReveal>

        <div className="border-t border-border">
          {WHY_MATTERS?.map((item, i) => (
            <ScrollReveal key={item?.label}>
              <div
                className="grid grid-cols-[40px_1fr_1fr] md:grid-cols-[80px_1fr_1.5fr] gap-4 md:gap-10 py-6 border-b border-border items-start"
              >
                <span
                  style={{
                    fontSize: '0.55rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    color: 'rgba(170,125,83,0.35)',
                  }}
                >
                  {String(i + 1)?.padStart(2, '0')}
                </span>
                <span
                  style={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'var(--foreground)',
                  }}
                >
                  {item?.label}
                </span>
                <span style={{ fontSize: '0.875rem', color: 'var(--secondary)', lineHeight: 1.6 }}>
                  {item?.note}
                </span>
              </div>
            </ScrollReveal>
          ))}
        </div>

        <ScrollReveal>
          <div
            className="mt-10 p-6 border"
            style={{ borderColor: 'var(--border)', backgroundColor: 'rgba(255,255,255,0.4)' }}
          >
            <p style={{ fontSize: '0.875rem', color: 'var(--secondary)', lineHeight: 1.7 }}>
              Willow does not claim that every component can always be replaced under every circumstance. What we aim for is a system where replacement is possible without unnecessary damage — and where the process supports that goal from the beginning.
            </p>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

// ─── MANUFACTURING CONNECTION ─────────────────────────────────────────────────

function ManufacturingConnectionSection() {
  return (
    <section
      aria-labelledby="mfg-connection-heading"
      className="bg-warm-white border-b border-border"
    >
      <div className="container-willow py-16 md:py-24">
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-12 lg:gap-20 items-center">

          <ScrollReveal>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="accent-line" aria-hidden="true" />
                <span className="text-section-label">MANUFACTURING</span>
              </div>
              <h2
                id="mfg-connection-heading"
                style={{
                  fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)',
                  fontWeight: 700,
                  lineHeight: 1.05,
                  letterSpacing: '-0.035em',
                  marginBottom: '1.5rem',
                }}
              >
                Modularity Begins<br />on the Factory Floor.
              </h2>
              <p className="text-body-lg mb-5">
                Willow&apos;s approach to modularity is directly connected to its manufacturing capability. The way components are designed, documented and produced determines whether they can be serviced later.
              </p>
              <p className="text-body mb-8" style={{ color: 'var(--secondary)' }}>
                Manufacturing is not separate from the modularity philosophy. It is where the philosophy becomes real.
              </p>

              <Link
                href="/manufacturing"
                className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide transition-opacity duration-150 hover:opacity-70"
                style={{ color: 'var(--primary)' }}
              >
                Explore Manufacturing
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
            </div>
          </ScrollReveal>

          <ScrollReveal>
            <div className="relative overflow-hidden" style={{ aspectRatio: '3/4' }}>
              <SitePhoto photo={SITE_PHOTOS.coldPress} aspectRatio="3/4" className="w-full h-full" />
            </div>
          </ScrollReveal>

        </div>
      </div>
    </section>
  );
}

// ─── HOME INTERIORS CONNECTION ────────────────────────────────────────────────

// A finished home where our modular kitchen and crockery unit sit within the full interior
const HI_PHOTO = projectPhoto('blue-on-mars-apartment-banashankari', 'teal-l-shaped-modular-kitchen-with-lit-glass-crockery-unit.jpg');

function HomeInteriorsConnectionSection() {
  return (
    <section
      aria-labelledby="hi-connection-heading"
      style={{ backgroundColor: '#171614' }}
      className="border-b border-border"
    >
      <div className="container-willow py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          <ScrollReveal>
            <div className="relative overflow-hidden" style={{ aspectRatio: '4/3' }}>
              <SitePhoto photo={HI_PHOTO} aspectRatio="4/3" className="w-full h-full" sizes="(min-width: 1024px) 50vw, 100vw" />
              <div
                className="absolute bottom-0 left-0 right-0 px-6 py-4"
                style={{ background: 'linear-gradient(to top, rgba(23,22,20,0.7) 0%, transparent 100%)' }}
              >
                <p className="text-section-label" style={{ color: 'var(--primary)', fontSize: '0.55rem' }}>
                  WILLOW HOME INTERIORS · BANGALORE
                </p>
                <Link href={HI_PHOTO.projectHref} className="text-xs hover:underline" style={{ color: '#FAF8F5' }}>
                  {HI_PHOTO.projectName} →
                </Link>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="block h-px w-8 flex-shrink-0" style={{ backgroundColor: '#AA7D53' }} aria-hidden="true" />
                <span className="text-section-label" style={{ color: 'var(--primary)' }}>HOME INTERIORS</span>
              </div>
              <h2
                id="hi-connection-heading"
                style={{
                  color: '#FAF8F5',
                  fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                  fontWeight: 700,
                  lineHeight: 1.1,
                  letterSpacing: '-0.03em',
                  marginBottom: '1.5rem',
                }}
              >
                Applied to Every<br />Willow Home Interior.
              </h2>
              <p style={{ color: 'rgba(250,248,245,0.65)', fontSize: '1rem', lineHeight: 1.7, marginBottom: '1.25rem' }}>
                This approach to modularity is applied to the modular products used in Willow home interiors — kitchens, wardrobes, TV units, crockery units, beds, vanities and more.
              </p>
              <p style={{ color: 'rgba(250,248,245,0.45)', fontSize: '0.9375rem', lineHeight: 1.7, marginBottom: '2rem' }}>
                The goal is not just a beautiful interior at handover. It is an interior that remains manageable over its useful life.
              </p>

              <Link
                href="/home-interiors"
                className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide transition-opacity duration-150 hover:opacity-70"
                style={{ color: 'var(--primary)' }}
              >
                Explore Home Interiors
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
            </div>
          </ScrollReveal>

        </div>
      </div>
    </section>
  );
}

// ─── LEARNING APPROACH ────────────────────────────────────────────────────────

function LearningApproachSection() {
  return (
    <section
      aria-labelledby="learning-heading"
      style={{ backgroundColor: '#F0ECE6' }}
      className="border-b border-border"
    >
      <div className="container-willow py-16 md:py-24">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 lg:gap-20 items-start">

          <ScrollReveal>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="accent-line" aria-hidden="true" />
                <span className="text-section-label">WILLOW&apos;S APPROACH</span>
              </div>
              <h2
                id="learning-heading"
                style={{
                  fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)',
                  fontWeight: 700,
                  lineHeight: 1.05,
                  letterSpacing: '-0.035em',
                }}
              >
                We Decided Not to Make the Same Mistake Twice.
              </h2>
            </div>
          </ScrollReveal>

          <ScrollReveal>
            <div className="flex flex-col gap-6">
              <p className="text-body-lg">
                Willow&apos;s approach to modularity is not theoretical. It developed through real project execution — through problems encountered, investigated and documented.
              </p>
              <p className="text-body" style={{ color: 'var(--secondary)' }}>
                When a process fails, a material underperforms or an execution problem repeats, we investigate what happened, document the learning and improve the system. That is how the modularity philosophy was refined — not in a design studio, but on real projects.
              </p>

              <div
                className="p-5 border-l-2"
                style={{ borderColor: 'var(--primary)', backgroundColor: 'rgba(255,255,255,0.5)' }}
              >
                <p style={{ fontSize: '0.875rem', color: 'var(--secondary)', lineHeight: 1.7 }}>
                  That approach to manufacturing and execution eventually contributed to{' '}
                  <strong style={{ color: 'var(--foreground)' }}>EIS — Ennezz Interior Standards</strong>.
                  {' '}EIS is an internal framework developed through real project work — not an official universal industry standard.
                </p>
              </div>
            </div>
          </ScrollReveal>

        </div>
      </div>
    </section>
  );
}

// ─── FINAL CTA ────────────────────────────────────────────────────────────────

function FinalCTASection() {
  return (
    <section
      aria-labelledby="final-cta-heading"
      style={{ backgroundColor: '#171614' }}
    >
      <div className="container-willow py-20 md:py-28">
        <ScrollReveal>
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="block h-px w-8 flex-shrink-0" style={{ backgroundColor: '#AA7D53' }} aria-hidden="true" />
              <span className="text-section-label" style={{ color: 'var(--primary)' }}>START YOUR PROJECT</span>
            </div>
            <h2
              id="final-cta-heading"
              style={{
                color: '#FAF8F5',
                fontSize: 'clamp(1.75rem, 4vw, 3.25rem)',
                fontWeight: 700,
                lineHeight: 1.05,
                letterSpacing: '-0.04em',
                marginBottom: '1.25rem',
              }}
            >
              Planning Your Home Interiors in Bangalore?
            </h2>
            <p style={{ color: 'rgba(250,248,245,0.55)', fontSize: '1.0625rem', lineHeight: 1.7, marginBottom: '2.5rem' }}>
              Tell us what you&apos;re planning. We&apos;ll start by understanding the project.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <CTAPrimary label="Start Your Project" href="/contact" />
              <CTASecondary label="Explore Home Interiors" href="/home-interiors" />
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
